import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Alert,
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { BaseClass } from '../../api/BaseClass';

const apiRoot = process.env.REACT_APP_API_URL || '';

async function getAuthorizationHeaders() {
    const token = await new BaseClass().getAccessToken();
    return { Authorization: 'Bearer ' + token };
}

function formatPhoneNumber(value) {
    const digits = (value || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return value;
    return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
}

export default function SmsGreetingControls({ phoneNumberId }) {
    const [senders, setSenders] = useState([]);
    const [displayName, setDisplayName] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const loadSenders = useCallback(async () => {
        if (!phoneNumberId) return;

        try {
            const headers = await getAuthorizationHeaders();
            const response = await axios.get(apiRoot + '/api/PhoneNumber/GetSmsCommandSenders', {
                headers,
                params: { phoneNumberId }
            });
            setSenders(response.data);
        } catch (error) {
            setMessage({ severity: 'error', text: error.response?.data || 'Unable to load authorized mobile numbers.' });
        }
    }, [phoneNumberId]);

    useEffect(() => {
        loadSenders();
    }, [loadSenders]);

    async function addSender() {
        setBusy(true);
        setMessage(null);

        try {
            const headers = await getAuthorizationHeaders();
            await axios.post(
                apiRoot + '/api/PhoneNumber/AddSmsCommandSender',
                { displayName, mobileNumber },
                { headers, params: { phoneNumberId } }
            );
            setDisplayName('');
            setMobileNumber('');
            await loadSenders();
            setMessage({ severity: 'success', text: 'Mobile number authorized for greeting commands.' });
        } catch (error) {
            setMessage({ severity: 'error', text: error.response?.data || 'Unable to authorize that mobile number.' });
        } finally {
            setBusy(false);
        }
    }

    async function deleteSender(smsCommandSenderId) {
        setBusy(true);
        setMessage(null);

        try {
            const headers = await getAuthorizationHeaders();
            await axios.delete(apiRoot + '/api/PhoneNumber/DeleteSmsCommandSender', {
                headers,
                params: { phoneNumberId, smsCommandSenderId }
            });
            await loadSenders();
            setMessage({ severity: 'success', text: 'Mobile number removed.' });
        } catch (error) {
            setMessage({ severity: 'error', text: error.response?.data || 'Unable to remove that mobile number.' });
        } finally {
            setBusy(false);
        }
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 3, mb: 3 }}>
            <Typography variant="h6">Greeting by Text</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
                GREETING plays a temporary spoken message before the store&apos;s normal phone greeting.
                <br />Commands:
                <br /><code>GREETING "message"</code>
                <br /><code>GREETING UNTIL 5PM "message"</code>
                <br /><code>GREETING UNTIL 5:30 PM "message"</code>
                <br /><code>GREETING UNTIL TOMORROW 9AM "message"</code>
                <br /><code>GREETING CLEAR</code>
                <br /><code>GREETING STATUS</code>
                <br /><code>HELP</code>
                <br />An expiration without <code>TOMORROW</code> means today in the store&apos;s timezone. Greeting text must be in quotes.
            </Typography>

            {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}

            <List dense disablePadding sx={{ mb: 2 }}>
                {senders.map((sender) => (
                    <ListItem
                        key={sender.smsCommandSenderId}
                        secondaryAction={
                            <IconButton
                                edge="end"
                                aria-label="Remove authorized mobile number"
                                disabled={busy}
                                onClick={() => deleteSender(sender.smsCommandSenderId)}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        }
                    >
                        <ListItemText
                            primary={sender.displayName || formatPhoneNumber(sender.mobileNumber)}
                            secondary={sender.displayName ? formatPhoneNumber(sender.mobileNumber) : null}
                        />
                    </ListItem>
                ))}
                {!senders.length && <ListItem><ListItemText primary="No authorized mobile numbers" /></ListItem>}
            </List>

            <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                        label="Manager name"
                        size="small"
                        value={displayName}
                        onChange={(event) => setDisplayName(event.target.value)}
                    />
                    <TextField
                        label="Mobile number"
                        size="small"
                        value={mobileNumber}
                        onChange={(event) => setMobileNumber(event.target.value)}
                    />
                    <Button
                        variant="contained"
                        disabled={busy || !mobileNumber.trim()}
                        onClick={addSender}
                    >
                        Authorize
                    </Button>
                </Stack>
            </Box>
        </Paper>
    );
}
